import { orderBy } from 'firebase/firestore';
import type { Entity } from '@marmon/shared/types/common';
import { PageSize, SortDir } from '@marmon/shared/types/pagination';
import type {
	DocumentData,
	DocumentReference,
	DocumentSnapshot,
	QueryConstraint,
} from 'firebase/firestore';

export type PaginatedItem<T extends Partial<Entity>> = {
	data: T;
	ref: DocumentReference<T>;
	id: string;
};

export type PaginatedOptions<T extends Partial<Entity>> = {
	sortBy: keyof T | null;
	sortDir: SortDir;
	readonly constraints: QueryConstraint[];

	/**
	 * some queries require a parent to reference the collection of
	 */
	parentRef?: DocumentReference | null;
};

export interface PaginatedObjectType<
	T extends Partial<Entity>,
> extends PaginatedOptions<T> {
	pageSize: PageSize;
	totalElements: number | null;
	items: PaginatedItem<T>[];
	last?: DocumentSnapshot<T, DocumentData> | null;
}

export class PaginatedObject<
	T extends Partial<Entity>,
> implements PaginatedObjectType<T> {
	pageSize: PageSize = 10;

	totalElements: number | null = null;

	sortBy: keyof T | null = 'createdAt';
	sortDir: SortDir = 'desc';

	parentRef: DocumentReference | null = null;

	items: PaginatedItem<T>[] = [];

	last: DocumentSnapshot<T> | null = null;

	private _constraints: QueryConstraint[] = [];

	constructor(paginated?: Partial<PaginatedObjectType<T>>) {
		Object.assign(this, PaginatedDefault(), paginated);
	}

	get hasMore() {
		if (!this.totalElements) return false;
		return this.items.length < this.totalElements;
	}

	get constraints() {
		return [...this.defaultConstraints, ...this._constraints];
	}

	private set constraints(value: QueryConstraint[]) {
		this._constraints = value;
	}

	private get defaultConstraints() {
		const value: QueryConstraint[] = [];
		if (this.sortBy && this.sortDir) {
			value.push(orderBy(this.sortBy as string, this.sortDir));
		}
		return value;
	}

	update(paginated: Partial<PaginatedObjectType<T>>) {
		Object.assign(this, paginated);
		return this;
	}

	add(items: PaginatedItem<T>[]) {
		this.items.push(...items);
		return this;
	}

	reset() {
		this.items = [];
		this.last = null;
		this.totalElements = 0;
		return this;
	}
}

const PaginatedDefault = (): Partial<PaginatedObjectType<Partial<Entity>>> => ({
	items: [],
	last: null,
	pageSize: 10,
	totalElements: null,
	sortBy: 'createdAt',
	sortDir: 'asc',
	constraints: [],
});
